import React, { memo } from 'react';
import { Circle, X, ArrowRight, Search, AlertCircle, Info } from 'lucide-react';
import { highlightSegments } from '../../palette/fuzzy';
import { httpMethodLabelClass } from '../../utils/httpMethodLabelClass';
import UserAvatar from '../ui/UserAvatar';

export function Highlighted({ text, ranges, className }) {
  const segs = highlightSegments(text, ranges || []);
  return (
    <span className={className}>
      {segs.map((s, i) => (s.hit ? <mark key={i} className="palette-hit">{s.text}</mark> : <React.Fragment key={i}>{s.text}</React.Fragment>))}
    </span>
  );
}

function rangesFor(result, field) {
  return (result.matches || []).find((m) => m.field === field)?.ranges || [];
}

/** "3m", "2h", "1d" — compact, language-neutral, like the mockups. */
export function relativeTime(ts, now = Date.now()) {
  if (!Number.isFinite(ts) || ts <= 0) return '';
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 60) return `${Math.max(1, s)}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  if (d < 365) return `${d}d`;
  return `${Math.round(d / 365)}y`;
}

function Lead({ result }) {
  if (result.avatarUserId) {
    return <UserAvatar userId={result.avatarUserId} name={result.title} size={20} className="palette-avatar" />;
  }
  if (result.method) {
    const m = String(result.method).toUpperCase();
    return <span className={`palette-method command-palette-method ${httpMethodLabelClass(m)}`}>{m}</span>;
  }
  const Icon = result.icon || Circle;
  return (
    <span className="palette-icon" aria-hidden="true">
      <Icon size={15} strokeWidth={1.9} />
    </span>
  );
}

function Trailing({ result, section, t, onForget }) {
  const p = result.payload || {};
  const parts = [];
  if (result.badge) parts.push(<span key="b" className="command-palette-scope-badge palette-badge">{result.badge}</span>);
  if (p.prefix) parts.push(<span key="pre" className="palette-prefix">{p.prefix}</span>);
  if (result.keyLabel) parts.push(<Highlighted key="k" className="palette-key" text={result.keyLabel} ranges={rangesFor(result, 'keyLabel')} />);
  if (p.shortcutLabel) parts.push(<kbd key="s" className="palette-kbd command-palette-shortcut">{p.shortcutLabel}</kbd>);
  if (p.scopeProviderId) parts.push(<span key="tab" className="palette-tabhint">{t('palette.hint.tabScope')}</span>);
  if (result.ts && (section === 'recent' || result.provider === 'messages' || result.provider === 'people')) {
    parts.push(<span key="ts" className="palette-time">{relativeTime(result.ts)}</span>);
  }
  if (section === 'recent' && onForget) {
    parts.push(
      <button
        key="x"
        type="button"
        tabIndex={-1}
        className="palette-forget"
        aria-label={t('palette.recent.remove', { name: result.title })}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          onForget(result);
        }}
      >
        <X size={12} aria-hidden="true" />
      </button>
    );
  }
  return parts.length ? <span className="palette-trailing">{parts}</span> : null;
}

/**
 * One list item. Rows are `role="option"` (§2.7); headers, notes and the
 * skeleton are presentational. Keeps the old `command-palette-row*` class
 * names, which the existing e2e specs select on.
 */
function PaletteRow({ item, id, active, onHover, onRun, onContext, onForget, t, style }) {
  if (item.type === 'header') {
    return (
      <div className="palette-header command-palette-group-label" id={id} role="presentation" style={style}>
        {item.label}
      </div>
    );
  }
  if (item.type === 'skeleton') {
    return (
      <div className="palette-skeleton" role="presentation" style={style} aria-hidden="true">
        <span className="palette-skeleton-bar" />
      </div>
    );
  }
  if (item.type === 'note' || item.type === 'error' || item.type === 'empty') {
    const Icon = item.type === 'error' ? AlertCircle : Info;
    return (
      <div className={`palette-note palette-note--${item.type}`} role="presentation" style={style}>
        <Icon size={13} aria-hidden="true" />
        <span>{item.label}</span>
      </div>
    );
  }
  const common = {
    id,
    role: 'option',
    'aria-selected': active,
    'data-active': active ? 'true' : 'false',
    className: `palette-row command-palette-row${active ? ' command-palette-row-active palette-row--active' : ''}`,
    style,
    onMouseMove: onHover,
    onMouseDown: (e) => e.preventDefault(),
    onClick: onRun,
  };
  if (item.type === 'more' || item.type === 'searchMessages') {
    const Icon = item.type === 'more' ? ArrowRight : Search;
    return (
      <div {...common}>
        <span className="palette-icon" aria-hidden="true">
          <Icon size={15} />
        </span>
        <span className="palette-text">
          <span className="palette-title command-palette-row-title">{item.label}</span>
        </span>
        <span className="palette-trailing">
          <kbd className="palette-kbd">↵</kbd>
        </span>
      </div>
    );
  }
  const r = item.result;
  return (
    <div
      {...common}
      className={`${common.className}${r.snippet ? ' palette-row--snippet' : ''}`}
      onContextMenu={(e) => {
        e.preventDefault();
        onContext?.();
      }}
    >
      <Lead result={r} />
      <span className="palette-text">
        <span className="palette-line">
          <Highlighted className="palette-title command-palette-row-title" text={r.title} ranges={rangesFor(r, 'title')} />
          {r.subtitle ? <Highlighted className="palette-subtitle command-palette-row-subtitle" text={r.subtitle} ranges={rangesFor(r, 'subtitle')} /> : null}
        </span>
        {r.snippet ? <Highlighted className="palette-snippet" text={r.snippet.text} ranges={r.snippet.ranges} /> : null}
      </span>
      <Trailing result={r} section={item.section} t={t} onForget={onForget} />
    </div>
  );
}

export default memo(PaletteRow);
